import { useEffect, useRef } from "react";
import { useDrag } from "react-dnd";
import { getEmptyImage } from "react-dnd-html5-backend";
import { ItemTypes } from "../../constants";
import { Creative } from "../../schemas/creativeSchema";
import { getFullMediaUrl } from "../../utils/mediaUtils";

const DraggableCreative = ({
  creative,
  getDragItem,
}: {
  creative: Creative | any;
  // Returns the drag payload for this creative. Lets the parent ship the
  // whole multi-selection when a selected item is dragged. Defaults to just
  // this creative when not provided.
  getDragItem?: (creative: Creative | any) => any;
}) => {
  const ref = useRef<HTMLDivElement>(null);

  const [{ isDragging }, drag, preview] = useDrag(() => ({
    type: ItemTypes.CREATIVE,
    item: () => (getDragItem ? getDragItem(creative) : { items: [creative] }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }));

  // Hide the default HTML5 drag ghost so CustomDragLayer renders the preview
  // (with a multi-item count badge) instead.
  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  drag(ref);

  // Helper function to determine if media is video or image
  const getMediaType = (): "video" | "image" | "unknown" => {
    if (!creative.media || creative.media.length === 0) {
      return "unknown";
    }

    const firstMedia = creative.media[0];
    const format = firstMedia.format?.toLowerCase() || "";
    const fileName = firstMedia.fileName?.toLowerCase() || "";

    // Check if it's a video format
    if (
      format.includes("video") ||
      format.includes("mp4") ||
      format.includes("mov") ||
      format.includes("avi") ||
      format.includes("mkv") ||
      format.includes("webm") ||
      fileName.includes(".mp4") ||
      fileName.includes(".mov") ||
      fileName.includes(".avi") ||
      fileName.includes(".mkv") ||
      fileName.includes(".webm")
    ) {
      return "video";
    }

    // Check if it's an image format
    if (
      format.includes("image") ||
      format.includes("jpg") ||
      format.includes("jpeg") ||
      format.includes("png") ||
      format.includes("gif") ||
      format.includes("webp") ||
      format.includes("svg") ||
      fileName.includes(".jpg") ||
      fileName.includes(".jpeg") ||
      fileName.includes(".png") ||
      fileName.includes(".gif") ||
      fileName.includes(".webp") ||
      fileName.includes(".svg")
    ) {
      return "image";
    }

    return "unknown";
  };

  // Get the media URL to display
  const getMediaUrl = (): string | null => {
    if (!creative.media || creative.media.length === 0) {
      return null;
    }

    const firstMedia = creative.media[0];
    const mediaType = getMediaType();

    if (mediaType === "video") {
      // For video, prefer thumbnail URL
      return (
        getFullMediaUrl(firstMedia.thumbnailUrl) ||
        getFullMediaUrl(firstMedia.fileUrl) ||
        null
      );
    } else if (mediaType === "image") {
      // For image, use the file URL
      return getFullMediaUrl(firstMedia.fileUrl) || null;
    }

    return null;
  };

  const mediaType = getMediaType();
  const mediaUrl = getMediaUrl();
  const fallbackIcon = "assets/images/creative-icon.png";
  const displayName = creative.media?.[0]?.fileName || creative.uniqueId;

  return (
    <div
      ref={ref}
      className="flex flex-col items-center p-3 border bg-white shadow-sm rounded-lg cursor-move hover:shadow-md transition-shadow"
      style={{ opacity: isDragging ? 0.5 : 1 }}
      title={displayName}
    >
      {mediaUrl && (mediaType === "video" || mediaType === "image") ? (
        <div className="relative h-20 w-20 mb-1 rounded overflow-hidden bg-gray-100 flex items-center justify-center">
          <img
            src={mediaUrl}
            className="h-full w-full object-cover"
            alt={displayName}
            onError={(e) => {
              // Fallback to icon if image fails to load
              const target = e.target as HTMLImageElement;
              target.src = fallbackIcon;
            }}
          />
        </div>
      ) : (
        <img
          src={fallbackIcon}
          className="h-20 w-20 text-primary-default mb-1"
          alt={displayName}
        />
      )}
      <span className="text-sm text-gray-700 font-medium text-center truncate w-full">
        {displayName}
      </span>
    </div>
  );
};

export default DraggableCreative;
